import { useTranslation } from "react-i18next";
import { Input } from "frontend-components";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { faMagnifyingGlass, faXmark } from "@fortawesome/sharp-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { indexToMonth } from "@dashboard/utils/months";
import type { AnnualCycleFilters } from "@dashboard/hooks/useAnnualCycleTable";
import { NewActivityButton } from "./NewActivityButton";

type HeaderProps = {
    filters: AnnualCycleFilters;
    onFilterChange: <K extends keyof AnnualCycleFilters>(
        key: K,
        value: AnnualCycleFilters[K],
    ) => void;
    onClearFilters: () => void;
    hasFilter: <K extends keyof AnnualCycleFilters>(key: K) => boolean;
    hasAnyFilter: () => boolean;
};

export default function AnnualCycleToolbar({
    filters,
    onFilterChange,
    onClearFilters,
    hasFilter,
    hasAnyFilter,
}: HeaderProps) {
    const { t } = useTranslation();

    const getMonthName = (monthIndex: number): string =>
        t(`common.month.long.${indexToMonth[monthIndex + 1]}`);
    const chipClass =
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-pill border border-compendia-border bg-compendia-greige-20 text-compendia-foreground text-xs hover:bg-compendia-greige-40 transition-colors";

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center gap-3">
                <div className="relative w-full flex-1 xl:max-w-xs">
                    <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-chameleon-green pointer-events-none"
                    />
                    <Input
                        value={filters.search}
                        onChange={(e) => onFilterChange("search", e.target.value)}
                        placeholder={t("common.searchActivities")}
                        className="pl-9 w-full"
                    />
                </div>
                <NewActivityButton iconOnly className="xl:hidden" />
            </div>

            {hasAnyFilter() && (
                <div className="flex gap-2 flex-wrap items-center">
                    <button type="button" className={chipClass} onClick={onClearFilters}>
                        {t("common.clear_filter")}
                        <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                    {hasFilter("selectedMonths") &&
                        filters.selectedMonths.map((month) => (
                            <button
                                key={month}
                                type="button"
                                onClick={() => {
                                    const newMonths = filters.selectedMonths.filter(
                                        (m) => m !== month,
                                    );
                                    onFilterChange("selectedMonths", newMonths);
                                }}
                                className={chipClass}
                            >
                                {getMonthName(month)}
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        ))}
                    {hasFilter("search") && (
                        <button
                            type="button"
                            onClick={() => onFilterChange("search", "")}
                            className={chipClass}
                        >
                            {t("common.search") + ": "} &quot;{filters.search}&quot;
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
